import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const sections = [
  {
    title: "Our Commitment",
    body: [
      "ClickNido exists to make booking trusted, verified home and business services simple, transparent and reliable — for customers and for the professionals who deliver the work.",
      "This policy sets out how we expect everyone on the platform — customers, service partners, and our own team — to conduct themselves.",
    ],
  },
  {
    title: "Non-Discrimination",
    body: [
      "ClickNido does not tolerate discrimination on the basis of religion, caste, gender, sexual orientation, disability, or any other protected characteristic — by customers toward partners, by partners toward customers, or within our own team.",
      "Reports of discriminatory behaviour are taken seriously and may result in account suspension.",
    ],
  },
  {
    title: "Code of Conduct",
    body: [
      "Customers are expected to treat service partners with respect, provide safe access to the service location, and communicate honestly about the work required.",
      "Service partners are expected to arrive as scheduled, perform work to a professional standard, and treat customers and their property with care.",
      "Abusive language, harassment, or unsafe behaviour by either party may result in the booking being cancelled and the account being reviewed.",
    ],
  },
  {
    title: "Safety & Trust",
    body: [
      "Service partners listed on ClickNido go through a verification process before they're allowed to accept bookings.",
      "If you ever feel unsafe during a booking, you can end it and contact our support team immediately.",
    ],
  },
  {
    title: "Data & Privacy",
    body: [
      "How we collect, use, and protect your information is covered in detail in our Privacy Policy.",
    ],
  },
];

const CompanyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.breadcrumb}>Home &nbsp;›&nbsp; Company Policy</div>

        <h1 style={styles.title}>Company Policy</h1>
        <p style={styles.intro}>
          An overview of how ClickNido operates, and what we expect from everyone
          who uses the platform.
        </p>

        {sections.map((sec) => (
          <div key={sec.title} style={styles.card}>
            <h2 style={styles.cardTitle}>{sec.title}</h2>
            <ul style={styles.list}>
              {sec.body.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        ))}

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Related Policies</h2>
          <div style={styles.linkRow}>
            <button style={styles.inlineLink} onClick={() => navigate("/privacy-policy")}>
              Privacy Policy <ArrowRight size={14} />
            </button>
            <button style={styles.inlineLink} onClick={() => navigate("/terms-conditions")}>
              Terms &amp; Conditions <ArrowRight size={14} />
            </button>
          </div>
        </div>

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Contact Us</h2>
          <p style={styles.contactText}>Questions about this policy? Reach out to us anytime.</p>
          <button style={styles.inlineLink} onClick={() => navigate("/contact-us")}>
            Contact Us <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    background: "#f5f6fb",
    minHeight: "100vh",
    padding: "40px 16px",
    fontFamily:
      "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: "#111827",
  },
  container: { maxWidth: "860px", margin: "0 auto" },
  breadcrumb: { color: "#7b8198", fontSize: "13px", fontWeight: 600, marginBottom: "14px" },
  title: { margin: 0, fontSize: "32px", fontWeight: 800, color: "#1c2148", marginBottom: "12px" },
  intro: { margin: "0 0 28px", fontSize: "15px", lineHeight: 1.75, color: "#5c6480", maxWidth: "700px" },
  card: {
    background: "#fff",
    borderRadius: "20px",
    padding: "26px",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 28px rgba(16,24,40,0.04)",
    marginBottom: "18px",
  },
  cardTitle: { margin: "0 0 14px", fontSize: "17px", fontWeight: 800, color: "#21284d" },
  list: { margin: 0, paddingLeft: "20px", color: "#5c6480", fontSize: "14px", lineHeight: 1.9 },
  contactText: { margin: "0 0 14px", fontSize: "14px", lineHeight: 1.8, color: "#5c6480" },
  linkRow: { display: "flex", gap: "22px", flexWrap: "wrap" },
  inlineLink: {
    background: "transparent",
    border: "none",
    color: "#6d5efc",
    fontWeight: 700,
    fontSize: "14px",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
    padding: 0,
  },
};

export default CompanyPolicy;
